import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail, clearLegacyAuthStorage } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = logged
  const [error, setError] = useState("");

  useEffect(() => {
    // SEC-003: ensure no stale JS-readable tokens remain from older versions.
    clearLegacyAuthStorage();
    // Skip auth probing on public routes to avoid unnecessary /auth/me → refresh → redirect chain
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.startsWith("/join")) { setUser(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      // Login response may include access_token/refresh_token in body for
      // non-browser clients. The SPA ignores them: cookies carry the session.
      const { data } = await api.post("/auth/login", { email, password });
      clearLegacyAuthStorage();
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearLegacyAuthStorage();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, error }),
    [user, login, logout, error]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
